import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import * as XLSX from 'xlsx';

import { CustomTitleComponent } from '@app/shared/components/custom-title/custom-title.component';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { InvoiceDataService } from '@app/services/entities/invoice.service';
import { ExternalInvoiceService } from '@app/services/entities/external-invoice.service';
import { InvoiceMonitorService } from '../invoice-monitor.component.service';
import { Factura, ExternalInvoice } from '@app/types';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';

interface ValidRow {
  facturaId: string;
  folioFiscal: string;
  receptorRFC: string;
  nuevoEstatus: string;
  observaciones: string;
}

interface ErrorRow extends ValidRow {
  errorReason: string;
}

@Component({
  selector: 'app-upload-status-dialog',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule, CustomTitleComponent],
  templateUrl: './upload-status-dialog.component.html',
  styleUrls: ['./upload-status-dialog.component.scss']
})
export class UploadStatusDialogComponent implements OnInit {
  isLoading = false;
  isSaving = false;
  fileName = '';
  step: 'upload' | 'preview' | 'result' = 'upload';
  previewTab: 'valid' | 'error' = 'valid';
  totalRowsInFile = 0;
  allInvoices: (Factura | ExternalInvoice)[] = []; // Tipo genérico
  isAvediMonitor: boolean;

  saveResult: { applied: number; failedGroups: number; failedDetails: string[] } | null = null;

  // Datos para las tablas
  validDataSource = new MatTableDataSource<ValidRow>([]);
  errorDataSource = new MatTableDataSource<ErrorRow>([]);

  validDisplayedColumns: string[] = ['folioFiscal', 'receptorRFC', 'nuevoEstatus', 'observaciones'];
  errorDisplayedColumns: string[] = ['folioFiscal', 'receptorRFC', 'nuevoEstatus', 'errorReason'];

  @ViewChild('validPaginator') validPaginator!: MatPaginator;
  @ViewChild('errorPaginator') errorPaginator!: MatPaginator;

  constructor(
    public dialogRef: MatDialogRef<UploadStatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { allInvoices: (Factura | ExternalInvoice)[], isAvedi: boolean },
    private snackBar: CustomSnackBarService,
    // --- MODIFICACIÓN: Inyectar ambos servicios ---
    private invoiceDataService: InvoiceDataService,
    private externalInvoiceService: ExternalInvoiceService,
    private invoiceMonitorService: InvoiceMonitorService,
    private statusTranslator: StatusTranslatorService
  ) {
    // --- MODIFICACIÓN: Recibir datos del inyector ---
    this.allInvoices = data.allInvoices;
    this.isAvediMonitor = data.isAvedi;
  }

  ngOnInit(): void {
    // Ya no es necesario cargar facturas aquí
  }

  downloadTemplate(): void {
    const headers = [['Folio Fiscal', 'Receptor RFC', 'Nuevo Estatus', 'Observaciones']];
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(headers);

    // Estilo para los encabezados
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "3D6091" } }
    };

    // Aplicar estilo a cada celda del encabezado
    const headerRange = XLSX.utils.decode_range(ws['!ref']!);
    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[address]) continue;
      ws[address].s = headerStyle;
    }

    // Definir anchos de columna
    ws['!cols'] = [
      { wch: 40 }, // Folio Fiscal
      { wch: 20 }, // Receptor RFC
      { wch: 25 }, // Nuevo Estatus
      { wch: 50 }  // Observaciones
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'Plantilla_Carga_Estatus.xlsx');
    this.snackBar.open('Plantilla descargada.', 'success');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        this.snackBar.open('Por favor, seleccione un archivo .xlsx', 'error');
        return;
      }
      this.fileName = file.name;
      this.validDataSource.data = [];
      this.errorDataSource.data = [];
      this.processFile(file);
    }
    input.value = ''; // Reset input para permitir cargar el mismo archivo de nuevo
  }

  onTabChange(tab: 'valid' | 'error'): void {
    this.previewTab = tab;
    this.attachPaginator();
  }

  private attachPaginator(): void {
    setTimeout(() => {
      if (this.previewTab === 'valid' && this.validPaginator) {
        this.validDataSource.paginator = this.validPaginator;
      } else if (this.previewTab === 'error' && this.errorPaginator) {
        this.errorDataSource.paginator = this.errorPaginator;
      }
    });
  }

  goBackToUpload(): void {
    this.step = 'upload';
    this.fileName = '';
    this.validDataSource.data = [];
    this.errorDataSource.data = [];
  }

  get totalAppliedRows(): number {
    return this.saveResult ? this.saveResult.applied : 0;
  }

  private processFile(file: File): void {
    this.isLoading = true;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });
        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as string[][];

        const validRows: ValidRow[] = [];
        const errorRows: ErrorRow[] = [];

        if (data.length < 2) {
          this.snackBar.open('El archivo está vacío o no contiene datos.', 'warning');
          this.isLoading = false;
          return;
        }

        const headerMap: { [key: string]: number } = {};
        (data[0] || []).forEach((header, index) => {
          const normalizedHeader = String(header).trim().toLowerCase().replace(/\s+/g, '');
          if (normalizedHeader.includes('foliofiscal')) headerMap['folioFiscal'] = index;
          if (normalizedHeader.includes('receptorrfc')) headerMap['receptorRFC'] = index;
          if (normalizedHeader.includes('nuevoestatus')) headerMap['nuevoEstatus'] = index;
          if (normalizedHeader.includes('observaciones')) headerMap['observaciones'] = index;
        });

        const requiredHeaders = ['folioFiscal', 'receptorRFC', 'nuevoEstatus', 'observaciones'];
        if (!requiredHeaders.every(h => h in headerMap)) {
          this.snackBar.open('El archivo no tiene los encabezados requeridos (Folio Fiscal, Receptor RFC, Nuevo Estatus, Observaciones).', 'error');
          this.isLoading = false;
          return;
        }

        // Detectar claves duplicadas (misma factura dos veces en el sistema)
        const keyCounts = new Map<string, number>();
        this.allInvoices.forEach(inv => {
          const key = `${inv.uuid}-${inv.receptorRFC}`;
          keyCounts.set(key, (keyCounts.get(key) || 0) + 1);
        });

        const invoiceMap = new Map<string, Factura | ExternalInvoice>();
        this.allInvoices.forEach(inv => invoiceMap.set(`${inv.uuid}-${inv.receptorRFC}`, inv));

        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          if (row.every(cell => String(cell).trim() === '')) {
            continue; // Ignorar filas completamente vacías
          }

          const rowData: any = {
            folioFiscal: String(row[headerMap['folioFiscal']]).trim(),
            receptorRFC: String(row[headerMap['receptorRFC']]).trim(),
            nuevoEstatus: String(row[headerMap['nuevoEstatus']]).trim(),
            observaciones: String(row[headerMap['observaciones']]).trim()
          };

          const { folioFiscal, receptorRFC, nuevoEstatus, observaciones } = rowData;

          if (!folioFiscal || !receptorRFC || !nuevoEstatus) {
            errorRows.push({ ...rowData, errorReason: 'Faltan datos en columnas requeridas (Folio, RFC o Estatus).' });
            continue;
          }
          if (!observaciones) {
            errorRows.push({ ...rowData, errorReason: 'La columna de observaciones no puede estar vacía.' });
            continue;
          }

          const key = `${folioFiscal}-${receptorRFC}`;
          const invoice = invoiceMap.get(key);
          const validStatusList = this.statusTranslator.getStatuses()()
            .filter(s => !s.deleted)
            .sort((a, b) => a.order - b.order)
            .map(s => s.status);
          const validStatus = validStatusList.find(s => s.toLowerCase() === nuevoEstatus.toLowerCase());

          if (!invoice) {
            errorRows.push({ ...rowData, errorReason: 'Factura no encontrada (UUID o RFC no coinciden).' });
          } else if ((keyCounts.get(key) || 0) > 1) {
            // Datos duplicados en el sistema: el match es ambiguo, no se actualiza
            errorRows.push({ ...rowData, errorReason: `Existen ${(keyCounts.get(key) || 0)} facturas con el mismo UUID+RFC. Corrija el duplicado antes de actualizar por plantilla.` });
          } else if (!validStatus) {
            errorRows.push({ ...rowData, errorReason: `Estatus '${nuevoEstatus}' no es válido.` });
          } else if (invoice.estatus === validStatus) {
            errorRows.push({ ...rowData, errorReason: 'La factura ya se encuentra en este estatus.' });
          } else {
            validRows.push({ facturaId: invoice.id, folioFiscal, receptorRFC, nuevoEstatus: validStatus, observaciones });
          }
        }

        this.validDataSource.data = validRows;
        this.errorDataSource.data = errorRows;
        this.totalRowsInFile = validRows.length + errorRows.length;
        this.previewTab = 'valid';
        this.step = 'preview';
        this.attachPaginator();
        this.snackBar.open(`Archivo procesado: ${validRows.length} válidos, ${errorRows.length} errores.`, 'info');

      } catch (error) {
        this.snackBar.open('Error al leer el archivo Excel. Verifique el formato.', 'error');
        console.error("Error processing Excel file:", error);
      } finally {
        this.isLoading = false;
      }
    };

    reader.readAsBinaryString(file);
  }

  applyFilter(event: Event, dataSource: MatTableDataSource<any>): void {
    const filterValue = (event.target as HTMLInputElement).value;
    dataSource.filter = filterValue.trim().toLowerCase();
  }

  async saveChanges(): Promise<void> {
    this.isSaving = true;
    const validRows = this.validDataSource.data;

    if (validRows.length === 0) {
      this.snackBar.open('No hay cambios válidos para guardar.', 'warning');
      this.isSaving = false;
      return;
    }

    // Agrupar por estatus + observación para respetar la observación de cada fila
    const grouped = validRows.reduce((acc, row) => {
      const key = `${row.nuevoEstatus}||${row.observaciones}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(row);
      return acc;
    }, {} as Record<string, ValidRow[]>);

    try {
      // --- MODIFICACIÓN: Usar el servicio correcto basado en el contexto ---
      const targetService = this.isAvediMonitor ? this.invoiceDataService : this.externalInvoiceService;

      let applied = 0;
      const failedGroups: string[] = [];

      for (const key in grouped) {
        const rows = grouped[key];
        const [status, observation] = key.split('||');
        const ids = rows.map(r => r.facturaId);

        try {
          if (this.isAvediMonitor) {
            await (targetService as InvoiceDataService).aplicarCambioEstatus(ids, status, observation).toPromise();
          } else {
            await (targetService as ExternalInvoiceService).changeStatus(ids, status, observation).toPromise();
          }
          applied += ids.length;
        } catch (err) {
          // Reporte por grupo: los grupos previos ya quedaron aplicados
          failedGroups.push(`${status} (${ids.length} factura(s), obs: "${observation}")`);
        }
      }

      this.saveResult = { applied, failedGroups: failedGroups.length, failedDetails: failedGroups };
      this.step = 'result';

      if (failedGroups.length > 0) {
        this.snackBar.open(
          `${applied} cambio(s) aplicados. Fallaron ${failedGroups.length} grupo(s).`,
          'error',
          8000
        );
      } else {
        this.snackBar.open('Todos los cambios válidos se han guardado exitosamente.', 'success');
      }

    } catch (error) {
      this.snackBar.open('Ocurrió un error al guardar algunos cambios.', 'error');
    } finally {
      this.isSaving = false;
    }
  }

  closeDialog(result: boolean = false): void {
    this.dialogRef.close(result);
  }

  downloadErrors(event: MouseEvent): void {
    event.stopPropagation(); // Evita que el acordeón se expanda/colapse

    if (this.errorDataSource.data.length === 0) {
      this.snackBar.open('No hay errores para descargar.', 'info');
      return;
    }

    const dataToExport = this.errorDataSource.data.map(row => ({
      folioFiscal: row.folioFiscal,
      receptorRFC: row.receptorRFC,
      nuevoEstatus: row.nuevoEstatus,
      observaciones: row.observaciones,
      motivoError: row.errorReason
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Errores');
    XLSX.writeFile(wb, 'Errores_Carga_Estatus.xlsx');
  }
}